/**
 * Chat Panel Component
 *
 * The main chat interface with:
 * - Message history
 * - Input with live validation
 * - AI suggestions
 * - Ghost text autocomplete
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { Send, Sparkles, Loader2, AlertCircle, Paperclip, Compass, ArrowRight, MessageCircle, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useDiscoveryAssistantStore, type LocalMessage } from '@/stores/discoveryAssistantStore'
import { useAIStore } from '@/stores/aiStore'
import { discoveryApi } from '@/lib/api/client'
import {
  instantAssessment,
  assessAnswerQuality,
  getSuggestions,
  generateAssistantResponse,
  type AISuggestion,
} from '@/lib/ai/conversationAI'
import { QualityIndicator } from './QualityIndicator'
import { PollMessage } from './PollMessage'
import { MultiStepQuestionMessage } from './MultiStepQuestionMessage'
import { DocumentProcessingDialog } from '@/components/document-processing'
import { useCoachWorkflow } from '@/hooks/useCoachWorkflow'
import type { Document2WithEntities } from '@/lib/api/client'

interface ChatPanelProps {
  accountId: string
  projectId: string
  className?: string
}

export function ChatPanel({ accountId, projectId, className }: ChatPanelProps) {
  const {
    session,
    messages,
    inputText,
    validation,
    isSending,
    workflowSteps,
    isWorkflowLoaded,
    getCurrentStep,
    setInputText,
    setValidation,
    sendMessage,
    saveAnswer,
    addLocalMessage,
    updateStreamingMessage,
    addSource,
  } = useDiscoveryAssistantStore()

  const { apiKey, coachingModel } = useAIStore()

  // Coach workflow integration (workflow-driven AI coaching)
  const {
    workflow: coachWorkflow,
    steps: coachSteps,
    currentStep: currentCoachStep,
    coachState,
    streamingState: coachStreamingState,
    isConfigured: coachConfigured,
    processAnswer: processCoachAnswer,
  } = useCoachWorkflow(session?.id)

  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const [showDocumentDialog, setShowDocumentDialog] = useState(false)
  const [useCoachMode, setUseCoachMode] = useState(false) // Toggle for coach workflow mode

  // Compute contextual placeholder based on conversation state
  const getContextualPlaceholder = useCallback((): string => {
    if (!session) return 'Start a discovery session first...'

    // If in coach mode with a current step, show step-specific placeholder
    if (useCoachMode && currentCoachStep) {
      return `Share your thoughts about ${currentCoachStep.name.toLowerCase()}...`
    }

    // Check the last message to determine context
    const lastMessage = messages[messages.length - 1]
    if (!lastMessage) {
      return 'Share what you\'d like to discover today...'
    }

    // If last message was from assistant with a question, prompt for answer
    if (lastMessage.role === 'assistant' && !lastMessage.isStreaming) {
      const content = lastMessage.content.toLowerCase()
      if (content.includes('?')) {
        return 'Type your response...'
      }
      // If it has a poll, don't show text placeholder
      if (lastMessage.pollData || content.includes('json:poll')) {
        return 'Select an option above or type your own response...'
      }
      return 'Continue the conversation...'
    }

    // If last message was from user, waiting for AI
    if (lastMessage.role === 'user') {
      return 'Waiting for response...'
    }

    // If AI is streaming
    if (lastMessage.isStreaming) {
      return 'AI is thinking...'
    }

    // Default based on workflow step
    const currentStep = getCurrentStep()
    if (currentStep) {
      return `Tell me about ${currentStep.name.toLowerCase()}...`
    }

    return 'Type your answer...'
  }, [session, messages, useCoachMode, currentCoachStep, getCurrentStep])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // INSTANT validation as user types (no debounce, client-side only)
  useEffect(() => {
    if (!session || inputText.length < 5) {
      setValidation({ assessment: null, isValidating: false })
      return
    }

    // Run instant client-side assessment - no API call, instant feedback
    const instant = instantAssessment(inputText, parseInt(session.currentQuestion))
    setValidation({ assessment: instant, isValidating: false })

    // Optionally enrich with AI after longer pause (only if API key available)
    if (apiKey && inputText.length >= 20) {
      // Clear previous timeout
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }

      // Longer debounce for AI enrichment (user has paused typing)
      validationTimeoutRef.current = setTimeout(async () => {
        try {
          const aiAssessment = await assessAnswerQuality(
            { apiKey, model: coachingModel },
            inputText,
            parseInt(session.currentQuestion),
            getAnswersAsRecord()
          )
          // Only update if AI gives different score (enrichment)
          if (aiAssessment.score !== instant.score || aiAssessment.suggestions.length > 0) {
            setValidation({ assessment: aiAssessment, isValidating: false })
          }
        } catch (error) {
          console.error('AI validation error:', error)
          // Keep instant assessment on error
        }
      }, 1500) // Longer delay for AI
    }

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
      }
    }
  }, [inputText, apiKey, session?.currentQuestion, coachingModel])

  // Get answers as record for context
  const getAnswersAsRecord = useCallback((): Record<number, string> => {
    const answers = useDiscoveryAssistantStore.getState().answers
    const record: Record<number, string> = {}
    answers.forEach((answer, qNum) => {
      record[parseInt(qNum)] = answer.answer
    })
    return record
  }, [])

  // Load AI suggestions
  const loadSuggestions = useCallback(async () => {
    if (!apiKey || !session || inputText.length < 5) return

    setIsLoadingSuggestions(true)
    setShowSuggestions(true)

    try {
      const suggestions = await getSuggestions(
        { apiKey, model: coachingModel },
        inputText,
        parseInt(session.currentQuestion),
        getAnswersAsRecord()
      )
      setValidation({ suggestions })
    } catch (error) {
      console.error('Suggestions error:', error)
    } finally {
      setIsLoadingSuggestions(false)
    }
  }, [apiKey, session?.currentQuestion, inputText, coachingModel, getAnswersAsRecord])

  // Handle sending message
  const handleSend = async () => {
    if (!inputText.trim() || isSending || !session) return

    const content = inputText.trim()
    const questionNumber = session.currentQuestion
    const quality = validation.assessment?.score

    // Send user message
    await sendMessage(accountId, projectId, content)

    // Save as answer
    if (quality) {
      await saveAnswer(accountId, projectId, questionNumber, content, quality)
    }

    // Generate AI response - use coach workflow if enabled
    if (apiKey) {
      // Use coach workflow mode (new backend-driven approach)
      if (useCoachMode && coachConfigured && currentCoachStep) {
        addLocalMessage({
          role: 'assistant',
          content: '',
          messageType: 'follow_up',
          isStreaming: true,
        })

        try {
          // Build conversation history for context
          const previousMessages = messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          }))

          // Process answer through coach workflow (assess + followup + extract)
          await processCoachAnswer(content, previousMessages)

          // Update message with streaming text from coach workflow
          const coachResponse = coachStreamingState.rawText || 'I understand your perspective. Let me ask some follow-up questions.'
          updateStreamingMessage(coachResponse)

          // Mark streaming complete
          const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
          const lastMsg = lastMessages[lastMessages.length - 1]
          if (lastMsg?.isStreaming) {
            lastMsg.isStreaming = false
            lastMsg.content = coachResponse
          }
        } catch (error) {
          console.error('Coach workflow error:', error)
          updateStreamingMessage('Sorry, I had trouble processing your answer. Please try again.')
          const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
          const lastMsg = lastMessages[lastMessages.length - 1]
          if (lastMsg?.isStreaming) {
            lastMsg.isStreaming = false
          }
        }
      } else {
        // Legacy mode - use existing workflow or client-side approach
        addLocalMessage({
          role: 'assistant',
          content: '',
          messageType: 'follow_up',
          isStreaming: true,
        })

        try {
          // Get current step for workflow-driven response
          const currentStep = getCurrentStep()

          // Use workflow-driven prompt if available, otherwise fall back to client-side
          if (isWorkflowLoaded && currentStep) {
            // Workflow-driven: use server-side executePrompt
            const result = await discoveryApi.executePrompt(
              accountId,
              projectId,
              session.id,
              {
                stepKey: currentStep.key,
                promptType: 'followup',
                userInput: content,
                apiKey,
              }
            )

            // Update the streaming message with the result
            updateStreamingMessage(result.text)

            // Mark streaming complete
            const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
            const lastMsg = lastMessages[lastMessages.length - 1]
            if (lastMsg?.isStreaming) {
              lastMsg.isStreaming = false
              lastMsg.content = result.text
            }
          } else {
            // Fallback: use client-side generateAssistantResponse
            const conversationHistory = messages.map(m => ({
              role: m.role as 'user' | 'assistant',
              content: m.content,
            }))
            conversationHistory.push({ role: 'user', content })

            // Accumulate streamed response
            let fullResponse = ''
            await generateAssistantResponse(
              { apiKey, model: coachingModel },
              conversationHistory,
              parseInt(questionNumber),
              getAnswersAsRecord(),
              (chunk) => {
                fullResponse += chunk
                updateStreamingMessage(fullResponse)
              }
            )

            // Update the last message to mark streaming complete
            const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
            const lastMsg = lastMessages[lastMessages.length - 1]
            if (lastMsg?.isStreaming) {
              lastMsg.isStreaming = false
              lastMsg.content = fullResponse
            }
          }

          // If quality is good enough, offer to advance
          if (quality && ['anecdotal', 'validated', 'bulletproof'].includes(quality)) {
            // Could add a "Continue to next question" button here
          }
        } catch (error) {
          console.error('Response generation error:', error)
          // Update streaming message to show error
          updateStreamingMessage('Sorry, I had trouble generating a response. Please try again.')
          const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
          const lastMsg = lastMessages[lastMessages.length - 1]
          if (lastMsg?.isStreaming) {
            lastMsg.isStreaming = false
          }
        }
      }
    }

    // Clear input and validation
    setInputText('')
    setValidation({ assessment: null, suggestions: [], ghostText: null })
    setShowSuggestions(false)
  }

  // Handle quick action buttons
  const handleQuickAction = useCallback((action: string) => {
    if (!inputRef.current) return

    switch (action) {
      case 'go_deeper':
        setInputText("I'd like to explore this topic more deeply. Can you help me understand the underlying factors or root causes?")
        inputRef.current.focus()
        break
      case 'move_on':
        setInputText("I'm satisfied with this topic. Let's move on to the next area we should discuss.")
        inputRef.current.focus()
        break
      case 'ask_question':
        setInputText('')
        inputRef.current.focus()
        inputRef.current.placeholder = 'Type your question...'
        break
    }
  }, [setInputText])

  // Handle document created
  const handleDocumentCreated = async (doc: Document2WithEntities) => {
    // Add a system message to notify about the document
    addLocalMessage({
      role: 'assistant',
      messageType: 'system',
      content: `I've analyzed **${doc.document.title}** and found **${doc.entities.length} entities** and **${doc.relationships.length} relationships**. These insights can help inform your discovery process. Would you like me to summarize the key findings?`,
    })

    // Link document to session by creating a DiscoverySource
    if (session) {
      await addSource(accountId, projectId, {
        sourceType: 'document',
        title: doc.document.title,
        content: doc.document.id, // Store document ID in content field
      })
    }
  }

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
    if (e.key === 'Tab' && validation.ghostText) {
      e.preventDefault()
      setInputText(inputText + validation.ghostText)
      setValidation({ ghostText: null })
    }
  }

  // Accept a suggestion
  const handleAcceptSuggestion = (suggestion: AISuggestion) => {
    if (suggestion.type === 'completion') {
      setInputText(inputText + ' ' + suggestion.text)
    } else {
      setInputText(suggestion.text)
    }
    setShowSuggestions(false)
    inputRef.current?.focus()
  }

  // Handle AI follow-up generation after poll/MCQ submission
  const handleAnswerSubmitted = useCallback(async (answerText: string) => {
    if (!apiKey || !session) return

    // Use coach workflow mode if enabled
    if (useCoachMode && coachConfigured && currentCoachStep) {
      addLocalMessage({
        role: 'assistant',
        content: '',
        messageType: 'follow_up',
        isStreaming: true,
      })

      try {
        // Build conversation history for context
        const previousMessages = messages.slice(-10).map(m => ({
          role: m.role,
          content: m.content,
        }))

        // Process answer through coach workflow (assess + followup + extract)
        await processCoachAnswer(answerText, previousMessages)

        // Update message with streaming text from coach workflow
        const coachResponse = coachStreamingState.rawText || 'I understand your perspective. Let me ask some follow-up questions.'
        updateStreamingMessage(coachResponse)

        // Mark streaming complete
        const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
        const lastMsg = lastMessages[lastMessages.length - 1]
        if (lastMsg?.isStreaming) {
          lastMsg.isStreaming = false
          lastMsg.content = coachResponse
        }
      } catch (error) {
        console.error('Coach workflow error:', error)
        updateStreamingMessage('Sorry, I had trouble processing your answer. Please try again.')
        const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
        const lastMsg = lastMessages[lastMessages.length - 1]
        if (lastMsg?.isStreaming) {
          lastMsg.isStreaming = false
        }
      }
    } else {
      // Legacy mode - use existing workflow or client-side approach
      addLocalMessage({
        role: 'assistant',
        content: '',
        messageType: 'follow_up',
        isStreaming: true,
      })

      try {
        // Get current step for workflow-driven response
        const currentStep = getCurrentStep()

        // Use workflow-driven prompt if available
        if (isWorkflowLoaded && currentStep) {
          const result = await discoveryApi.executePrompt(
            accountId,
            projectId,
            session.id,
            {
              stepKey: currentStep.key,
              promptType: 'followup',
              userInput: answerText,
              apiKey,
            }
          )

          updateStreamingMessage(result.text)

          const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
          const lastMsg = lastMessages[lastMessages.length - 1]
          if (lastMsg?.isStreaming) {
            lastMsg.isStreaming = false
            lastMsg.content = result.text
          }
        } else {
          // Fallback: use client-side generateAssistantResponse
          const conversationHistory = messages.map(m => ({
            role: m.role as 'user' | 'assistant',
            content: m.content,
          }))
          conversationHistory.push({ role: 'user', content: answerText })

          let fullResponse = ''
          await generateAssistantResponse(
            { apiKey, model: coachingModel },
            conversationHistory,
            parseInt(session.currentQuestion),
            getAnswersAsRecord(),
            (chunk) => {
              fullResponse += chunk
              updateStreamingMessage(fullResponse)
            }
          )

          const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
          const lastMsg = lastMessages[lastMessages.length - 1]
          if (lastMsg?.isStreaming) {
            lastMsg.isStreaming = false
            lastMsg.content = fullResponse
          }
        }
      } catch (error) {
        console.error('Response generation error:', error)
        updateStreamingMessage('Sorry, I had trouble generating a response. Please try again.')
        const lastMessages = [...useDiscoveryAssistantStore.getState().messages]
        const lastMsg = lastMessages[lastMessages.length - 1]
        if (lastMsg?.isStreaming) {
          lastMsg.isStreaming = false
        }
      }
    }
  }, [
    apiKey,
    session,
    useCoachMode,
    coachConfigured,
    currentCoachStep,
    processCoachAnswer,
    coachStreamingState.rawText,
    messages,
    addLocalMessage,
    updateStreamingMessage,
    getCurrentStep,
    isWorkflowLoaded,
    accountId,
    projectId,
    coachingModel,
    getAnswersAsRecord,
  ])

  return (
    <div className={cn('flex flex-col h-full min-h-0 bg-white rounded-lg shadow-sm border border-warm-200', className)}>
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => {
          // Find if this is the last assistant message (for showing quick actions)
          const isLastAssistantMessage = message.role === 'assistant' &&
            !messages.slice(index + 1).some(m => m.role === 'assistant')

          return (
            <MessageBubble
              key={message.id}
              message={message}
              accountId={accountId}
              projectId={projectId}
              onAnswerSubmitted={handleAnswerSubmitted}
              onQuickAction={handleQuickAction}
              isLastAssistantMessage={isLastAssistantMessage}
            />
          )
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Live validation panel - instant feedback */}
      {validation.assessment && inputText.length >= 5 && (
        <div className="px-4 py-2 border-t border-warm-100 bg-warm-50">
          <div className="flex items-center gap-3">
            <QualityIndicator
              score={validation.assessment.score}
              confidence={validation.assessment.confidence}
              size="sm"
            />
            {validation.assessment.suggestions.length > 0 && (
              <span className="text-xs text-warm-500 flex-1 truncate">
                {validation.assessment.suggestions[0]}
              </span>
            )}
            {validation.assessment.issues.length > 0 && validation.assessment.score === 'shallow' && (
              <span className="text-xs text-warm-400">
                {validation.assessment.issues[0]}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Suggestions panel */}
      {showSuggestions && (
        <div className="px-4 py-2 border-t border-warm-100 bg-gradient-to-b from-warm-50 to-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-warm-700">AI Suggestions</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSuggestions(false)}
              className="text-warm-500 h-6 px-2"
            >
              Close
            </Button>
          </div>
          {isLoadingSuggestions ? (
            <div className="flex items-center gap-2 text-warm-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Generating suggestions...</span>
            </div>
          ) : validation.suggestions.length > 0 ? (
            <div className="space-y-2">
              {validation.suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => handleAcceptSuggestion(suggestion)}
                  className="w-full text-left p-2 rounded-md border border-warm-200 hover:border-warm-400 hover:bg-warm-50 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <span className={cn(
                      'text-xs px-1.5 py-0.5 rounded font-medium',
                      suggestion.type === 'completion' && 'bg-blue-100 text-blue-700',
                      suggestion.type === 'improvement' && 'bg-green-100 text-green-700',
                      suggestion.type === 'alternative' && 'bg-amber-100 text-amber-700',
                    )}>
                      {suggestion.type}
                    </span>
                    <span className="text-sm text-warm-800 flex-1">{suggestion.text}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-warm-500">No suggestions available. Keep typing!</p>
          )}
        </div>
      )}

      {/* Coach workflow MCQ panel */}
      {useCoachMode && coachState.lastMCQOptions && coachState.lastMCQOptions.length > 0 && (
        <div className="px-4 py-3 border-t border-warm-100 bg-gradient-to-b from-solution/5 to-white">
          <div className="flex items-center gap-2 mb-2">
            <Compass className="h-4 w-4 text-solution" />
            <span className="text-sm font-medium text-warm-700">Follow-up Questions</span>
          </div>
          <div className="space-y-2">
            {coachState.lastMCQOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  setInputText(option.label)
                  inputRef.current?.focus()
                }}
                className="w-full text-left p-3 rounded-lg border border-warm-200 hover:border-solution hover:bg-solution/5 transition-colors"
              >
                <p className="text-sm font-medium text-warm-900">{option.label}</p>
                {option.description && (
                  <p className="text-xs text-warm-500 mt-1">{option.description}</p>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Coach workflow assessment panel */}
      {useCoachMode && coachState.lastAssessment && (
        <div className="px-4 py-2 border-t border-warm-100 bg-gradient-to-b from-solution/5 to-white">
          <div className="flex items-center gap-3">
            <div className={cn(
              'px-2 py-1 rounded text-xs font-medium',
              coachState.lastAssessment.quality === 'deep' && 'bg-green-100 text-green-700',
              coachState.lastAssessment.quality === 'good' && 'bg-blue-100 text-blue-700',
              coachState.lastAssessment.quality === 'shallow' && 'bg-amber-100 text-amber-700',
              coachState.lastAssessment.quality === 'acknowledged' && 'bg-warm-100 text-warm-700',
            )}>
              {coachState.lastAssessment.quality}
            </div>
            <span className="text-xs text-warm-600 flex-1">
              {coachState.lastAssessment.feedback}
            </span>
            <span className="text-xs text-warm-400">
              {coachState.lastAssessment.confidence}% confidence
            </span>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="p-4 border-t border-warm-200">
        <div className="relative">
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={getContextualPlaceholder()}
            rows={3}
            className="w-full resize-none rounded-lg border border-warm-300 px-4 py-3 pr-24 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            disabled={isSending || coachStreamingState.isStreaming || !session}
          />

          {/* Ghost text overlay */}
          {validation.ghostText && (
            <div className="absolute inset-0 pointer-events-none px-4 py-3 text-sm">
              <span className="invisible">{inputText}</span>
              <span className="text-warm-400">{validation.ghostText}</span>
              <span className="text-xs text-warm-400 ml-2">(Tab to accept)</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDocumentDialog(true)}
              className="h-8 px-2 text-warm-500 hover:text-primary"
              title="Upload document"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setUseCoachMode(!useCoachMode)}
              className={cn(
                "h-8 px-2",
                useCoachMode ? "text-solution bg-solution/10" : "text-warm-500 hover:text-primary"
              )}
              title={useCoachMode ? "Coach workflow enabled" : "Enable coach workflow"}
            >
              <Compass className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSuggestions}
              disabled={!apiKey || inputText.length < 5}
              className="h-8 px-2 text-warm-500 hover:text-primary"
              title="Get AI suggestions"
            >
              <Sparkles className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              onClick={handleSend}
              disabled={!inputText.trim() || isSending || coachStreamingState.isStreaming}
              className="h-8 px-3"
            >
              {isSending || coachStreamingState.isStreaming ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Quick tips */}
        <div className="mt-2 flex items-center gap-4 text-xs text-warm-500">
          <span>Enter to send</span>
          <span>Shift+Enter for new line</span>
          {!apiKey && (
            <span className="flex items-center gap-1 text-amber-600">
              <AlertCircle className="h-3 w-3" />
              Set API key in settings for AI features
            </span>
          )}
        </div>
      </div>

      {/* Document Upload Dialog */}
      <DocumentProcessingDialog
        isOpen={showDocumentDialog}
        onClose={() => setShowDocumentDialog(false)}
        onDocumentCreated={handleDocumentCreated}
        title="Upload Document to Discovery Session"
        description="Upload a document to extract insights that can inform your discovery process"
      />
    </div>
  )
}

// Message bubble component
function MessageBubble({
  message,
  accountId,
  projectId,
  onAnswerSubmitted,
  onQuickAction,
  isLastAssistantMessage,
}: {
  message: LocalMessage
  accountId: string
  projectId: string
  onAnswerSubmitted?: (answer: string) => void
  onQuickAction?: (action: string) => void
  isLastAssistantMessage?: boolean
}) {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'

  // Handle poll messages
  if (message.messageType === 'poll' && message.pollData) {
    return (
      <PollMessage
        message={message}
        accountId={accountId}
        projectId={projectId}
        onAnswerSubmitted={onAnswerSubmitted}
      />
    )
  }

  // Handle multi-step messages
  if (message.messageType === 'multi_step_question' && message.multiStepData) {
    return <MultiStepQuestionMessage message={message} accountId={accountId} projectId={projectId} />
  }

  // NEW: Check if message content contains embedded poll JSON (fallback for when store parsing fails)
  const pollMatch = message.content.match(/```json:poll\n([\s\S]*?)```/)
  if (pollMatch && !isUser) {
    try {
      const pollData = JSON.parse(pollMatch[1])
      // Create a synthetic message with pollData
      const pollMessage = {
        ...message,
        messageType: 'poll' as const,
        pollData,
      }
      return (
        <PollMessage
          message={pollMessage}
          accountId={accountId}
          projectId={projectId}
          onAnswerSubmitted={onAnswerSubmitted}
        />
      )
    } catch (e) {
      console.error('Failed to parse embedded poll JSON:', e)
      // Fall through to regular rendering if parsing fails
    }
  }

  // Check if this message ends with a question (no poll block)
  const contentWithoutPoll = stripPollBlocks(message.content)
  const showQuickActions = isLastAssistantMessage &&
    !isUser &&
    !isSystem &&
    !message.isStreaming &&
    !pollMatch &&
    contentWithoutPoll.trim().length > 0

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start', isSystem && 'items-center')}>
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-4 py-3',
          isUser && 'bg-primary text-white',
          !isUser && !isSystem && 'bg-warm-100 text-warm-900',
          isSystem && 'bg-warm-50 text-warm-600 text-sm italic',
          message.isStreaming && 'animate-pulse'
        )}
      >
        {/* Message type badge for assistant */}
        {!isUser && !isSystem && message.questionNumber && (
          <div className="text-xs text-warm-500 mb-1">
            Question {message.questionNumber}
          </div>
        )}

        {/* Content with markdown-like formatting */}
        <div className="whitespace-pre-wrap">
          {formatMessageContent(contentWithoutPoll)}
        </div>

        {/* Streaming indicator */}
        {message.isStreaming && message.content.length === 0 && (
          <div className="flex items-center gap-2 text-warm-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Thinking...</span>
          </div>
        )}
      </div>

      {/* Quick action buttons after assistant messages */}
      {showQuickActions && onQuickAction && (
        <div className="flex flex-wrap gap-2 mt-2 max-w-[80%]">
          <button
            onClick={() => onQuickAction('go_deeper')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-warm-200 bg-white text-warm-700 hover:bg-warm-50 hover:border-warm-300 transition-colors"
          >
            <Search className="h-3 w-3" />
            Go deeper
          </button>
          <button
            onClick={() => onQuickAction('move_on')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-warm-200 bg-white text-warm-700 hover:bg-warm-50 hover:border-warm-300 transition-colors"
          >
            <ArrowRight className="h-3 w-3" />
            Move on
          </button>
          <button
            onClick={() => onQuickAction('ask_question')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-warm-200 bg-white text-warm-700 hover:bg-warm-50 hover:border-warm-300 transition-colors"
          >
            <MessageCircle className="h-3 w-3" />
            Ask a question
          </button>
        </div>
      )}
    </div>
  )
}

// Strip poll/multi-step blocks from content
function stripPollBlocks(content: string): string {
  return content
    .replace(/```json:poll\n[\s\S]*?```/g, '')
    .replace(/```json:multi-step\n[\s\S]*?```/g, '')
    .trim()
}

// Simple markdown-like formatting
function formatMessageContent(content: string): React.ReactNode {
  // Bold: **text**
  const parts = content.split(/(\*\*.*?\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    return part
  })
}
